import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardBody, Button, Input, Select, DatePicker, Icon, showToast, Pagination } from '@repo/ui';
import { MatterSelectionTable } from './MatterSelectionTable';
import ColumnVisibilityModal from './ColumnVisibilityModal';
import {
  useTrustEligibleMatters,
  useClientsForFiltering,
  useCreateBulkTrustRequests,
} from '../queries';
import { formatCurrency } from '@xtnd/web/shared/utils/format';

// Custom threshold values for the slider
const THRESHOLD_VALUES = [
  0, 1000, 5000, 10000, 15000, 20000, 25000, 30000, 40000, 50000,
  60000, 70000, 80000, 90000, 100000, 125000, 150000, 200000
];

export function BulkCreationTab() {
  const [thresholdIndex, setThresholdIndex] = useState(0);
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [selectedMatterIds, setSelectedMatterIds] = useState<string[]>([]);
  const [selectedMatters, setSelectedMatters] = useState<string[]>([]);
  const [editableAmounts, setEditableAmounts] = useState<Record<string, number>>({});
  const [editableDates, setEditableDates] = useState<Record<string, { issueDate?: Date; dueDate?: Date }>>({});
  const [defaultIssueDate, setDefaultIssueDate] = useState<Date>(new Date());
  const [defaultDueDate, setDefaultDueDate] = useState<Date | null>(null);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('trust-requests-columns');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.warn('Failed to parse saved column visibility, using defaults');
      }
    }
    return {
      matterDisplayNumber: true,
      clientName: true,
      matterStatus: true,
      minimumThreshold: true,
      trustBalance: true,
      outstandingBalance: true,
      wip: true,
      calculatedTrustAmount: true,
      trustRequestAmount: true,
      issueDate: true,
      dueDate: true,
      lastPaymentDate: false,
      lastInvoiceDate: false,
      lastTrustRequestDate: false,
    };
  });
  const [columnVisibilityModalOpen, setColumnVisibilityModalOpen] = useState(false);
  const [batchName, setBatchName] = useState('');
  const [isSelectAllPages, setIsSelectAllPages] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentCursor, setCurrentCursor] = useState<string | undefined>(undefined);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);
  const [pageSize, setPageSize] = useState(() => {
    const saved = sessionStorage.getItem('trust-requests-page-size');
    if (saved) {
      try {
        const parsed = parseInt(saved, 10);
        return isNaN(parsed) ? 100 : parsed;
      } catch (e) {
        console.warn('Failed to parse saved page size, using default');
      }
    }
    return 100; // Default page size
  });

  // Get current threshold value from index
  const threshold = THRESHOLD_VALUES[thresholdIndex] ?? 0;

  // Calculate default due date (30 days from issue date)
  React.useEffect(() => {
    if (defaultIssueDate && !defaultDueDate) {
      const dueDate = new Date(defaultIssueDate);
      dueDate.setDate(dueDate.getDate() + 30);
      setDefaultDueDate(dueDate);
    }
  }, [defaultIssueDate, defaultDueDate]);

  // Save page size to sessionStorage whenever it changes
  React.useEffect(() => {
    sessionStorage.setItem('trust-requests-page-size', pageSize.toString());
  }, [pageSize]);

  // Save column visibility to localStorage whenever it changes
  React.useEffect(() => {
    localStorage.setItem('trust-requests-columns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  // Reset pagination state
  const resetPagination = () => {
    setCurrentPage(1);
    setCurrentCursor(undefined);
    setHasNextPage(false);
    setHasPreviousPage(false);
    setCursorHistory([]);
  };

  // Fetch clients for filter dropdown
  const { data: clientsData, isLoading: isLoadingClients } = useClientsForFiltering({
    hasOpenMatters: true,
  });

  // Fetch trust-eligible matters
  const { data: mattersData, isLoading: isLoadingMatters } = useTrustEligibleMatters({
    clientIds: selectedClientIds.length > 0 ? selectedClientIds : undefined,
    threshold: threshold > 0 ? threshold : undefined,
    limit: pageSize,
    cursor: currentCursor,
    sortBy: 'calculatedTrustAmount',
    sortOrder: 'desc',
  });

  // Create bulk trust requests mutation
  const { mutate: createBulkTrustRequests, isPending: isCreating } = useCreateBulkTrustRequests();

  // Transform clients data for Select component
  const clientOptions = useMemo(() => {
    if (!clientsData?.data) return [];
    return clientsData.data.map(client => ({
      value: client.id,
      label: `${client.name} (${client.matterCount} matters)`,
    }));
  }, [clientsData]);

  // Transform matters data for Select component
  const matterOptions = useMemo(() => {
    if (!mattersData?.data) return [];
    return mattersData.data.map(matter => ({
      value: matter.id,
      label: `${matter.matterDisplayNumber} - ${matter.clientName}`,
    }));
  }, [mattersData]);

  // Handle filter application
  const handleApplyFilters = () => {
    resetPagination();
    // Filters are already applied via query params, just reset pagination
    showToast.success('Filters applied');
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setThresholdIndex(0);
    setSelectedClientIds([]);
    setSelectedMatterIds([]);
    setSelectedMatters([]);
    setEditableAmounts({});
    setIsSelectAllPages(false);
    resetPagination();
    showToast.success('Filters cleared');
  };

  // Pagination handlers - cursor-based
  const handlePageChange = (page: number) => {
    if (page > currentPage) {
      handleNextPage();
    } else if (page < currentPage) {
      handlePreviousPage();
    }
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    resetPagination();
  };

  const handleNextPage = () => {
    if (hasNextPage && mattersData?.pagination.cursor) {
      const cursorToStore = currentCursor || 'FIRST_PAGE';
      setCursorHistory(prev => [...prev, cursorToStore]);

      setCurrentPage(prev => prev + 1);
      setCurrentCursor(mattersData.pagination.cursor);
      setHasPreviousPage(true);
    }
  };

  const handlePreviousPage = () => {
    if (hasPreviousPage && cursorHistory.length > 0) {
      const newHistory = [...cursorHistory];
      const previousCursor = newHistory.pop();

      setCursorHistory(newHistory);
      setCurrentPage(prev => prev - 1);

      if (previousCursor === 'FIRST_PAGE') {
        setCurrentCursor(undefined);
      } else {
        setCurrentCursor(previousCursor);
      }

      setHasNextPage(true);
      if (currentPage - 1 === 1) {
        setHasPreviousPage(false);
      }
    }
  };

  const handleSelectAllPages = (selectAll: boolean) => {
    setIsSelectAllPages(selectAll);
    if (selectAll) {
      // When selecting all pages, we conceptually select all matters
      // In a real implementation, this would need to be handled differently
      // For now, we'll just set the flag and handle it in the UI
      showToast.success(`Selected all ${totalMatters} matters across all pages`);
    } else {
      // When deselecting all pages, clear all selections
      setSelectedMatters([]);
      showToast.success('Cleared all selections');
    }
  };

  // Handle amount change for a specific matter
  const handleAmountChange = (matterId: string, amount: number) => {
    setEditableAmounts(prev => ({ ...prev, [matterId]: amount }));
  };

  // Handle date change for a specific matter
  const handleDateChange = (matterId: string, field: 'issueDate' | 'dueDate', date: Date | undefined) => {
    setEditableDates(prev => ({
      ...prev,
      [matterId]: {
        ...prev[matterId],
        [field]: date
      }
    }));
  };

  // Handle column visibility toggle
  const handleToggleColumn = (key: string) => {
    setVisibleColumns(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Handle toggle all columns
  const handleToggleAllColumns = (checked: boolean) => {
    const newVisibility = Object.keys(visibleColumns).reduce((acc, key) => {
      acc[key] = checked;
      return acc;
    }, {} as Record<string, boolean>);
    setVisibleColumns(newVisibility);
  };

  const getVisibleColumnCount = () => {
    // Only count optional columns that appear in the modal
    const optionalColumns = [
      'calculatedTrustAmount', 'issueDate', 'dueDate',
      'minimumThreshold', 'trustBalance', 'outstandingBalance', 'wip',
      'lastPaymentDate', 'lastInvoiceDate', 'lastTrustRequestDate'
    ];
    return optionalColumns.filter(key => visibleColumns[key]).length;
  };

  const getTotalColumnCount = () => {
    // Only count optional columns that appear in the modal
    const optionalColumns = [
      'calculatedTrustAmount', 'issueDate', 'dueDate',
      'minimumThreshold', 'trustBalance', 'outstandingBalance', 'wip',
      'lastPaymentDate', 'lastInvoiceDate', 'lastTrustRequestDate'
    ];
    return optionalColumns.length;
  };

  // Handle bulk creation
  const handleCreateSelected = async () => {
    const totalSelected = isSelectAllPages ? totalMatters : selectedMatters.length;

    if (totalSelected === 0) {
      showToast.error('Please select at least one matter');
      return;
    }

    if (isSelectAllPages) {
      showToast.error('Select all pages feature is not yet implemented. Please select individual matters.');
      return;
    }

    // Validate that all selected matters have amounts
    const mattersToCreate = mattersData?.data.filter(m => selectedMatters.includes(m.id)) || [];
    const invalidAmountMatters = mattersToCreate.filter(m => {
      const amount = editableAmounts[m.id] ?? m.calculatedTrustAmount;
      return !amount || amount <= 0;
    });

    if (invalidAmountMatters.length > 0) {
      showToast.error(`Please ensure all selected matters have valid amounts > $0`);
      return;
    }

    // Validate that each selected matter has valid dates
    const invalidDateMatters = mattersToCreate.filter(m => {
      const matterDates = editableDates[m.id];
      const issueDate = matterDates?.issueDate || defaultIssueDate;
      const dueDate = matterDates?.dueDate || defaultDueDate;

      return !issueDate || !dueDate || dueDate < issueDate;
    });

    if (invalidDateMatters.length > 0) {
      showToast.error(`Please ensure all selected matters have valid issue and due dates (due date must be after issue date)`);
      return;
    }

    // Generate batch code
    const batchCode = `batch-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const requestId = `bulk-tr-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

    // Prepare trust requests
    const trustRequests = mattersToCreate.map(matter => {
      const amount = editableAmounts[matter.id] ?? matter.calculatedTrustAmount;
      const matterDates = editableDates[matter.id];
      const issueDate = (matterDates?.issueDate || defaultIssueDate).toISOString().split('T')[0]!;
      const dueDate = (matterDates?.dueDate || defaultDueDate || new Date((matterDates?.issueDate || defaultIssueDate).getTime() + 30 * 24 * 60 * 60 * 1000)).toISOString().split('T')[0]!;

      return {
        matterId: matter.id,
        clientId: matter.clientId,
        amount,
        issueDate,
        dueDate,
        batchCode,
      };
    });

    // Create batch object
    const batch = {
      batchCode,
      name: batchName || `Bulk Trust Requests - ${new Date().toLocaleDateString()}`,
      createdAt: new Date().toISOString(),
      batchSize: trustRequests.length,
      appliedFilters: {
        clientIds: selectedClientIds.length > 0 ? selectedClientIds : undefined,
        threshold: threshold > 0 ? threshold : undefined,
      },
    };

    // Submit bulk creation
    createBulkTrustRequests(
      {
        batch,
        trustRequests,
        requestId,
      },
      {
        onSuccess: () => {
          // Reset selections after successful creation
          setSelectedMatters([]);
          setEditableAmounts({});
          setBatchName('');
        },
      }
    );
  };


  const allMatters = mattersData?.data || [];
  // Filter matters based on matter filter selection
  const matters = selectedMatterIds.length > 0
    ? allMatters.filter(matter => selectedMatterIds.includes(matter.id))
    : allMatters;
  const totalMatters = selectedMatterIds.length > 0 ? matters.length : (mattersData?.pagination.total || 0);
  const totalPages = selectedMatterIds.length > 0 ? 1 : (mattersData?.pagination.totalPages || 1);

  // Update pagination state from API response
  React.useEffect(() => {
    if (mattersData?.pagination) {
      setHasNextPage(mattersData.pagination.hasNextPage);
      setHasPreviousPage(mattersData.pagination.hasPrevPage);
    }
  }, [mattersData?.pagination]);

  return (
    <div className="space-y-6">
      {/* Filters Section */}
      <Card variant="outlined" padding="lg">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Icon name="HiFilter" size="md" className="text-xtnd-blue" />
            <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">Filters</h3>
          </div>
        </CardHeader>
        <CardBody>
          <div className="space-y-6">
            {/* Client Filter - Full Width */}
            <div className="space-y-2">
              <Select
                label="Client Filter"
                options={clientOptions}
                value={selectedClientIds.length > 0 ? selectedClientIds.map(id => ({ value: id, label: clientOptions.find(opt => opt.value === id)?.label || id })) : null}
                onChange={(value) => {
                  if (Array.isArray(value)) {
                    setSelectedClientIds(value.map(item => typeof item === 'string' ? item : (item as any)?.value));
                  } else if (value && typeof value === 'object' && 'value' in value) {
                    setSelectedClientIds([(value as any).value]);
                  } else {
                    setSelectedClientIds([]);
                  }
                }}
                placeholder="Select clients to filter matters..."
                multiple
                searchable
                clearable
                isLoading={isLoadingClients}
                fullWidth
              />
            </div>

            {/* Matter Filter - Full Width */}
            <div className="space-y-2">
              <Select
                label="Matter Filter"
                options={matterOptions}
                value={selectedMatterIds.length > 0 ? selectedMatterIds.map(id => ({ value: id, label: matterOptions.find(opt => opt.value === id)?.label || id })) : null}
                onChange={(value) => {
                  if (Array.isArray(value)) {
                    setSelectedMatterIds(value.map(item => typeof item === 'string' ? item : (item as any)?.value));
                  } else if (value && typeof value === 'object' && 'value' in value) {
                    setSelectedMatterIds([(value as any).value]);
                  } else {
                    setSelectedMatterIds([]);
                  }
                }}
                placeholder="Select specific matters to focus on..."
                multiple
                searchable
                clearable
                isLoading={isLoadingMatters}
                fullWidth
              />
            </div>

          </div>

          {/* Threshold Slider */}
          <div className="space-y-4 mt-6">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
                Filter by Calculated Trust Amount ≥ {formatCurrency(threshold)}
              </label>
            </div>
            <input
              type="range"
              min="0"
              max={THRESHOLD_VALUES.length - 1}
              step="1"
              value={thresholdIndex}
              onChange={(e) => setThresholdIndex(Number(e.target.value))}
              className="w-full h-2 bg-xtnd-light dark:bg-xtnd-dark-700 rounded-lg appearance-none cursor-pointer accent-xtnd-blue"
            />
            <p className="text-xs text-xtnd-dark-600 dark:text-xtnd-light-400">
              Only show matters where the calculated trust amount (Calc Amount column) is greater than or equal to the selected threshold
            </p>
          </div>


          {/* Filter Actions */}
          <div className="flex gap-3 mt-6">
            <Button variant="primary" onClick={handleApplyFilters} leftIcon={<Icon name="HiFilter" />}>
              Apply Filters
            </Button>
            <Button variant="outline" onClick={handleClearFilters} leftIcon={<Icon name="HiX" />}>
              Clear Filters
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Data Entry Section */}
      <Card variant="outlined" padding="lg">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Icon name="HiDocumentText" size="md" className="text-xtnd-blue" />
            <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">Data Entry</h3>
          </div>
        </CardHeader>
        <CardBody>
          <div className="space-y-6">
            {/* Date Controls - Responsive Grid */}
            <div className="grid gap-6 sm:grid-cols-2">
              {/* Issue Date */}
              <div className="space-y-2">
                <DatePicker
                  mode="single"
                  label="Issue Date"
                  value={defaultIssueDate}
                  onChange={(date) => date && setDefaultIssueDate(date)}
                />
              </div>

              {/* Due Date */}
              <div className="space-y-2">
                <DatePicker
                  mode="single"
                  label="Due Date"
                  value={defaultDueDate}
                  onChange={(date) => {
                    if (date && date < defaultIssueDate) {
                      showToast.error('Due date cannot be before issue date');
                      return;
                    }
                    setDefaultDueDate(date);
                  }}
                />
              </div>
            </div>

            {/* Batch Name Input */}
            <div className="space-y-2">
              <Input
                label="Batch Name (Optional)"
                value={batchName}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 50) {
                    setBatchName(value);
                  }
                }}
                placeholder="e.g., Q4 2025 Trust Requests"
                fullWidth
              />
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Table Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setColumnVisibilityModalOpen(true)}
            className="flex items-center space-x-2 hover:bg-gray-50 dark:hover:bg-gray-700"
            title="Edit columns"
          >
            <Icon name="HiViewGrid" className="h-4 w-4" />
            <span className="hidden sm:inline">Edit Columns</span>
            <span className="inline-flex items-center justify-center text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-1.5 py-0.5 rounded min-w-[2rem]">
              {getVisibleColumnCount()}/{getTotalColumnCount()}
            </span>
          </Button>
        </div>
        <p className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-400">
          Showing <span className="font-semibold text-xtnd-dark dark:text-xtnd-light">{totalMatters}</span> matters
          {selectedMatterIds.length > 0 && (
            <span className="ml-1 text-xtnd-blue">(filtered by matter selection)</span>
          )}
          requiring trust requests
          {(selectedMatters.length > 0 || isSelectAllPages) && (
            <span className="ml-2">
              • <span className="font-semibold text-xtnd-blue">
                {isSelectAllPages ? totalMatters : selectedMatters.length}
              </span> selected
              {isSelectAllPages && (
                <span className="text-xs text-xtnd-blue-600 ml-1">(all pages)</span>
              )}
            </span>
          )}
        </p>
      </div>

      {/* Results Summary */}
      <div className="flex items-center justify-end">

        <div className="flex gap-2">
          <Button
            variant="primary"
            onClick={handleCreateSelected}
            disabled={(selectedMatters.length === 0 && !isSelectAllPages) || isCreating}
            leftIcon={<Icon name="HiPaperAirplane" />}
            size="sm"
            loading={isCreating}
          >
            Create {isSelectAllPages ? `All (${totalMatters})` : selectedMatters.length > 0 ? `(${selectedMatters.length})` : 'Selected'} TRRs
          </Button>
        </div>
      </div>

      {/* Data Table */}
      {isLoadingMatters ? (
        <Card variant="outlined" padding="lg">
          <div className="text-center py-8">
            <Icon name="HiArrowPath" size="xl" className="mx-auto mb-4 animate-spin text-xtnd-blue" />
            <p className="text-xtnd-dark-600 dark:text-xtnd-light-400">Loading matters...</p>
          </div>
        </Card>
      ) : (
        <>
          <MatterSelectionTable
            matters={matters}
            selectedMatters={selectedMatters}
            onSelectionChange={setSelectedMatters}
            editableAmounts={editableAmounts}
            onAmountChange={handleAmountChange}
            editableDates={editableDates}
            onDateChange={handleDateChange}
            visibleColumns={visibleColumns}
            totalRecords={totalMatters}
            isSelectAllPages={selectedMatterIds.length > 0 ? false : isSelectAllPages} // Disable select all when filtering by specific matters
            onSelectAllPages={selectedMatterIds.length > 0 ? () => {} : handleSelectAllPages} // Disable when filtering
          />

          {/* Pagination - only show when not filtering by specific matters */}
          {selectedMatterIds.length === 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalRecords={totalMatters}
              hasNextPage={hasNextPage}
              hasPreviousPage={hasPreviousPage}
              isLoading={isLoadingMatters}
              pageSize={pageSize}
              pageSizeOptions={[25, 50, 100, 200, 500]}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              onNextPage={handleNextPage}
              onPreviousPage={handlePreviousPage}
              showPageSizeSelector={true}
              showRecordCount={true}
              cursorBased={true}
              currentItemsCount={matters.length}
            />
          )}
        </>
      )}

      {/* Column Visibility Modal */}
      <ColumnVisibilityModal
        isOpen={columnVisibilityModalOpen}
        onClose={() => setColumnVisibilityModalOpen(false)}
        visibleColumns={visibleColumns}
        onToggleColumn={handleToggleColumn}
        onToggleAllColumns={handleToggleAllColumns}
      />
    </div>
  );
}

